import { useEffect, useState } from "react";
import magic from "./magic";
import { writeHeaterParameter } from "./controllers/heaterParameters";
import "./HeaterPanel.css";

const runningStates = {
	0: "Off",
	1: "Starting",
	2: "Pre-heating",
	3: "Ignition Failed",
	4: "Ignited",
	5: "Running",
	6: "Skipped",
	7: "Stopping",
	8: "Cooling Down",
};

const errorStates = {
	0: "No Error",
	1: "No Error, but started",
	2: "Voltage too low",
	3: "Voltage too high",
	4: "Ignition plug failure",
	5: "Pump Failure",
	6: "Too hot",
	7: "Motor Failure",
	8: "Serial connection lost",
	9: "Fire is extinguished",
	10: "Temperature sensor failure",
};

function HeaterPanel({ visible = true }) {
	const [pump_hz, setPumpHz] = useState(0);
	const [target_pump_hz, setTargetPumpHz] = useState(0);
	const [target_power, setTargetPower] = useState(0);
	const [heat_exchanger_temp, setHeatExchangerTemp] = useState(0);
	const [running_state, setRunningState] = useState(0);
	const [error_state, setErrorState] = useState(0);

	const [temp, setTemp] = useState(null);
	const [ideal_temp, setIdealTemp] = useState(0);
	const [redundant_count, setRedundantCount] = useState(0);
	const [pump_counter, setPumpCounter] = useState(0);
	const [heater_timer, setHeaterTimer] = useState(0);
	const [climate_control_enabled, setClimateControlEnabled] = useState(0);
	const [turnoff_temp, setTurnoffTemp] = useState(22.0);
	const [uptime, setUptime] = useState(0);
	const [controllerFaults, setControllerFaults] = useState(0);

	useEffect(() => {
		const onHeater = (data) => {
			setPumpHz(data.pump_hz);
			setTargetPumpHz(data.target_pump_hz);
			setTargetPower(data.target_power);
			setHeatExchangerTemp(data.heat_exchanger_temp);
			setRunningState(data.running_state);
			setErrorState(data.error_state);
			setTemp(data.temp);
			setIdealTemp(data.ideal_temp);
			setRedundantCount(data.redundant_count);
			setPumpCounter(data.pump_counter);
			setHeaterTimer(data.heater_timer);
			setClimateControlEnabled(data.climate_control_enabled);
			setTurnoffTemp(data.turnoff_temp);
			setUptime(data.uptime);
			setControllerFaults(data.controller_faults || 0);
		};
		magic.bleEventDispatcher.on("heater", onHeater);
		return () => {
			magic.bleEventDispatcher.removeListener("heater", onHeater);
		};
	}, []);

	const formatUptime = (seconds) => {
		const days = Math.floor(seconds / 86400);
		const hours = Math.floor((seconds % 86400) / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const secs = seconds % 60;
		let result = "";
		if (days > 0) result += `${days}d `;
		if (hours > 0) result += `${hours}h `;
		if (minutes > 0) result += `${minutes}m `;
		if (secs > 0 || result === "") result += `${secs}s`;
		return result.trim();
	};

	const isRunning = running_state !== 0;
	const powerPercent = Math.round(((target_power - 8) / 27) * 100);
	const fuelRate = Math.round(pump_hz * 0.022 * 60 * 60);
	const fuelRateCost = ((fuelRate / 1000) * 1.80).toFixed(2);
	const fuelUsed = (pump_counter * 0.022).toFixed(1);
	const fuelUsedCost = ((pump_counter * 0.022 / 1000) * 1.80).toFixed(2);

	if (!visible) return null;

	return (
		<div className="heater-panel">
            {controllerFaults !== 0 && (
                <div className="heater-error-banner" role="alert">
                    {[
                        [1, "Temperature probe unavailable or stale"],
                        [2, "Room temperature exceeded 80°C"],
                        [4, "Heater communication unavailable"],
                        [8, "Heater reported a failed start or fault"],
                        [16, "Heater stopped unexpectedly"],
                        [32, "Heater did not acknowledge the start request"],
                    ].filter(([bit]) => controllerFaults & bit).map(([, text]) => text).join(". ")}
                    {". Heating request cancelled. Check the heater before starting again."}
                </div>
            )}
			{!target_pump_hz ? (
				<div className="heater-no-device">
					<div className="heater-no-device-icon">🔥</div>
					<div className="heater-no-device-text">No heater detected</div>
				</div>
			) : (
				<>
					{/* Status Header */}
					<div className={`heater-status-card ${isRunning ? 'running' : 'off'}`}>
						<div className="heater-status-left">
							<div className="heater-temp-display">
								<span className="heater-temp-value">{temp == null ? "Unavailable" : temp}</span>
								<span className="heater-temp-unit">{temp == null ? "" : "°C"}</span>
							</div>
							<div className="heater-status-label">{runningStates[running_state]}</div>
						</div>
						<button
							className={`heater-power-btn ${isRunning ? 'on' : 'off'}`}
							onClick={isRunning ? turnHeaterOff : turnHeaterOn}
						>
							<span className="heater-power-icon">⏻</span>
							<span className="heater-power-text">{isRunning ? 'Turn Off' : 'Turn On'}</span>
						</button>
					</div>

					{/* Error Banner */}
					{error_state > 1 && (
						<div className="heater-error-banner">
							<span className="heater-error-icon">⚠️</span>
							<span>{errorStates[error_state]}</span>
						</div>
					)}

					{/* Power Control */}
					<div className="heater-card">
						<div className="heater-card-header">Power Level</div>
						<div className="heater-stepper">
							<button
								className="heater-stepper-btn"
								onClick={() => setHeaterPower(target_power - 1)}
							>−</button>
							<div className="heater-stepper-value">
								<span className="heater-stepper-number">{powerPercent}</span>
								<span className="heater-stepper-unit">%</span>
							</div>
							<button
								className="heater-stepper-btn"
								onClick={() => setHeaterPower(target_power + 1)}
							>+</button>
						</div>
						<div className="heater-power-bar">
							<div className="heater-power-fill" style={{ width: `${Math.max(0, Math.min(100, powerPercent))}%` }}></div>
						</div>
					</div>

					{/* Climate Control */}
					<div className="heater-card">
						<div className="heater-card-row">
							<span className="heater-card-header">Climate Control</span>
							<button
								className={`heater-toggle-btn ${climate_control_enabled ? 'on' : 'off'}`}
								onClick={() => sendParameter("climate", climate_control_enabled ? 0 : 1)}
							>
								{climate_control_enabled ? 'On' : 'Off'}
							</button>
						</div>
						{!!climate_control_enabled && (
							<div className="heater-temp-control">
								<span className="heater-temp-label">Target Temperature</span>
								<div className="heater-stepper compact">
									<button
										className="heater-stepper-btn"
										onClick={() => setTargetTemp(ideal_temp - 0.5)}
									>−</button>
									<div className="heater-stepper-value">
										<span className="heater-stepper-number">{ideal_temp.toFixed(1)}</span>
										<span className="heater-stepper-unit">°C</span>
									</div>
									<button
										className="heater-stepper-btn"
										onClick={() => setTargetTemp(ideal_temp + 0.5)}
									>+</button>
								</div>
							</div>
						)}
					</div>

					{/* Turn-off Temperature */}
					<div className="heater-card">
						<div className="heater-temp-control">
							<span className="heater-temp-label">Auto Turn-off Temperature</span>
							<div className="heater-stepper compact">
								<button
									className="heater-stepper-btn"
									onClick={() => setTurnoffTempCmd(turnoff_temp - 0.5)}
								>−</button>
								<div className="heater-stepper-value">
									<span className="heater-stepper-number">{turnoff_temp.toFixed(1)}</span>
									<span className="heater-stepper-unit">°C</span>
								</div>
								<button
									className="heater-stepper-btn"
									onClick={() => setTurnoffTempCmd(turnoff_temp + 0.5)}
								>+</button>
							</div>
						</div>
					</div>

					{/* Stats Grid */}
					<div className="heater-stats-grid">
						<div className="heater-stat-item">
							<span className="heater-stat-label">Internal</span>
							<span className="heater-stat-value">{heat_exchanger_temp}°C</span>
						</div>
						<div className="heater-stat-item">
							<span className="heater-stat-label">Fuel Rate</span>
							<span className="heater-stat-value">{fuelRate} ml/hr</span>
							<span className="heater-stat-cost">£{fuelRateCost}/hr</span>
						</div>
						<div className="heater-stat-item">
							<span className="heater-stat-label">Pump</span>
							<span className="heater-stat-value">{Number(pump_hz).toFixed(1)} Hz</span>
						</div>
						<div className="heater-stat-item">
							<span className="heater-stat-label">Timer</span>
							<span className="heater-stat-value">{formatTime(heater_timer)}</span>
						</div>
					</div>

					{/* Fuel Usage */}
					<div className="heater-card">
						<div className="heater-card-row">
							<div>
								<div className="heater-stat-label">Fuel Used</div>
								<div className="heater-fuel-value">{fuelUsed} ml <span className="heater-fuel-cost">(£{fuelUsedCost})</span></div>
							</div>
							<button className="heater-reset-btn" onClick={resetFuelUsed}>Reset</button>
						</div>
					</div>

					{/* Footer Stats */}
					<div className="heater-footer">
						<span>Uptime: {formatUptime(uptime)}</span>
						{redundant_count > 0 && <span>Redundant: {redundant_count}</span>}
					</div>
				</>
			)}
		</div>
	);
}

async function sendParameter(name, value) {
	try {
		await writeHeaterParameter(name, value);
	} catch (error) {
		window.alert(`Heater change was not confirmed: ${error.message}. Check the current status before retrying.`);
	}
}

async function setHeaterTimer(timerMs) {
	await sendParameter("timer", timerMs);
}

async function turnHeaterOn() {
	if (!window.confirm("Are you sure you want to turn the heater on?")) {
		return;
	}
	const timerMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
	await setHeaterTimer(timerMs);
}

async function turnHeaterOff() {
	if (!window.confirm("Are you sure you want to turn the heater off?")) {
		return;
	}
	const timerMs = 0; // 0 milliseconds to turn off
	await setHeaterTimer(timerMs);
}

async function setHeaterPower(power) {
	await sendParameter("power", power);
}

async function resetFuelUsed() {
	if (window.confirm("Are you sure you want to reset fuel used to 0?")) {
		await sendParameter("pumpCounter", 0);
	}
}

function formatTime(ms) {
	const hours = Math.floor(ms / (1000 * 60 * 60));
	const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((ms % (1000 * 60)) / 1000);
	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

function setTargetTemp(newTemp) {
	return sendParameter("idealTemperature", Math.round(newTemp * 100));
}

function setTurnoffTempCmd(newTemp) {
	return sendParameter("turnoffTemperature", Math.round(newTemp * 100));
}

export default HeaterPanel;
